import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import useAuth from '../../hooks/useAuth';
import CreateOrderModal from './CreateOrderModal';
import './OrderDetailsModal.css';

const STATUS_OPTIONS = ['pending', 'researching', 'priced', 'declined'];

const STATUS_LABELS = {
  pending: 'Pending',
  researching: 'Researching',
  priced: 'Priced',
  converted: 'Converted',
  declined: 'Declined',
};

const STATUS_COLORS = {
  pending: '#7f8c8d',
  researching: '#FFA500',
  priced: '#8e44ad',
  converted: '#228B22',
  declined: '#c0392b',
};

const InquiryDetailsModal = ({ inquiry, isOpen, onClose, onSuccess }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isConvertModalOpen, setIsConvertModalOpen] = useState(false);

  useEffect(() => {
    if (inquiry) {
      setEditData({
        status: inquiry.status,
        quoted_price: inquiry.quoted_price || '',
        research_notes: inquiry.research_notes || '',
      });
      setError(null);
    }
  }, [inquiry]);

  if (!isOpen || !inquiry) return null;

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setEditData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);

    try {
      await api.patch(`/special-orders/inquiries/${inquiry.id}`, {
        status: editData.status,
        quoted_price: editData.quoted_price === '' ? null : editData.quoted_price,
        research_notes: editData.research_notes,
      });
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update inquiry');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this inquiry? This cannot be undone.')) return;

    setLoading(true);
    setError(null);

    try {
      await api.delete(`/special-orders/inquiries/${inquiry.id}`);
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete inquiry');
      setLoading(false);
    }
  };

  const handleOrderCreated = async (createdOrderResponse) => {
    try {
      await api.patch(`/special-orders/inquiries/${inquiry.id}`, {
        status: 'converted',
        converted_order_id: createdOrderResponse?.orderId,
      });
    } catch (err) {
      console.error('Failed to mark inquiry as converted:', err);
    }
    setIsConvertModalOpen(false);
    onSuccess?.();
    onClose();
  };

  const canConvert = inquiry.status !== 'converted' && inquiry.status !== 'declined';

  return (
    <>
      <div className={`modal-overlay ${isOpen ? 'active' : ''}`} onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Inquiry Details</h2>
            <button className="modal-close" onClick={onClose}>&times;</button>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="modal-body">
            <div className="order-header-info">
              <div className="info-item">
                <span className="label">Inquiry ID</span>
                <span className="value">{inquiry.id}</span>
              </div>
              <div className="info-item">
                <span className="label">Logged</span>
                <span className="value">{formatDate(inquiry.created_at)}</span>
              </div>
              <div className="info-item">
                <span className="label">Status</span>
                <span className="badge" style={{ backgroundColor: STATUS_COLORS[inquiry.status] }}>
                  {STATUS_LABELS[inquiry.status] || inquiry.status}
                </span>
              </div>
            </div>

            <div className="modal-section">
              <h4>Customer Information</h4>
              <div className="info-grid">
                <div className="info-item">
                  <span className="label">Name</span>
                  <span className="value">{inquiry.customer_name}</span>
                </div>
                {inquiry.customer_phone && (
                  <div className="info-item">
                    <span className="label">Phone</span>
                    <span className="value">{inquiry.customer_phone}</span>
                  </div>
                )}
                {inquiry.customer_email && (
                  <div className="info-item">
                    <span className="label">Email</span>
                    <span className="value">{inquiry.customer_email}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-section">
              <h4>Item Requested</h4>
              <div className="info-item full-width">
                <span className="value notes-text">{inquiry.item_description}</span>
              </div>
              {inquiry.intake_notes && (
                <div className="info-item full-width">
                  <span className="label">Intake Notes</span>
                  <span className="value notes-text">{inquiry.intake_notes}</span>
                </div>
              )}
            </div>

            <div className="modal-section">
              <h4>Research &amp; Pricing</h4>

              {inquiry.status === 'converted' ? (
                <div className="info-item">
                  <span className="label">Converted to Order</span>
                  <span className="value">#{inquiry.converted_order_id}</span>
                </div>
              ) : (
                <>
                  <div className="form-group-edit">
                    <label>Status</label>
                    <select name="status" value={editData.status} onChange={handleFieldChange}>
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group-edit">
                    <label>Quoted Price ($)</label>
                    <input
                      type="number"
                      name="quoted_price"
                      value={editData.quoted_price}
                      onChange={handleFieldChange}
                      min="0"
                      step="0.01"
                      placeholder="Enter once priced"
                    />
                  </div>

                  <div className="form-group-edit">
                    <label>Research Notes</label>
                    <textarea
                      name="research_notes"
                      value={editData.research_notes}
                      onChange={handleFieldChange}
                      rows="3"
                      placeholder="Can we get it? From where? Any lead time?"
                    ></textarea>
                  </div>
                </>
              )}

              {inquiry.requested_by_name && (
                <div className="info-item">
                  <span className="label">Logged By</span>
                  <span className="value">{inquiry.requested_by_name}</span>
                </div>
              )}
              {inquiry.handled_by_name && (
                <div className="info-item">
                  <span className="label">Handled By</span>
                  <span className="value">{inquiry.handled_by_name}</span>
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer">
            {isAdmin && (
              <button className="btn-delete" onClick={handleDelete} disabled={loading}>
                Delete
              </button>
            )}
            {canConvert && (
              <button
                className="btn-edit"
                onClick={() => setIsConvertModalOpen(true)}
                disabled={loading}
              >
                Convert to Order
              </button>
            )}
            {inquiry.status !== 'converted' && (
              <button className="btn-save" onClick={handleSave} disabled={loading}>
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            )}
            <button className="btn-close" onClick={onClose} disabled={loading}>
              Close
            </button>
          </div>
        </div>
      </div>

      <CreateOrderModal
        isOpen={isConvertModalOpen}
        onClose={() => setIsConvertModalOpen(false)}
        onSuccess={handleOrderCreated}
        initialData={{
          customer_name: inquiry.customer_name || '',
          customer_phone: inquiry.customer_phone || '',
          item_name: inquiry.item_description || '',
          price: inquiry.quoted_price || 0,
        }}
      />
    </>
  );
};

export default InquiryDetailsModal;
